import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  getDownloadCatalog,
  getSubscription,
  saveDownload,
  startCheckout,
  isCustomerTier,
  type CatalogApp,
  type DownloadAsset,
  type SubscriptionView,
} from "../api/accountApi";
import { accountErrorMessage } from "../api/accountErrors";
import { LotseMark } from "../components/Lotse";
import { MembershipDocument } from "../components/MembershipDocument";
import { Loading, MetaLine, Notice, Panel, Row, Section } from "../components/Portal";
import { IconBriefcase, IconDownload, IconExternal } from "../components/icons";
import { useAccountAuth } from "../contexts/AccountAuthContext";
import { productReady } from "../lib/accountFormat";
import { bestInstaller, detectOs, installerMeta, type OsFamily } from "../lib/installer";
import {
  APP_STORE_URL,
  LOTSE_PRICE,
  PRICE_BEFORE_PAYMENT,
  SUPPORT_EMAIL,
  YUBIKEY_SHOP_URL,
  planBlurb,
} from "../lib/portalCopy";

type LoadState = "loading" | "ready" | "error";

const DESKTOPS: Array<{ os: OsFamily; title: string; logo: string }> = [
  { os: "windows", title: "Windows", logo: "/images/platforms/windows.svg" },
  { os: "mac", title: "macOS", logo: "/images/platforms/apple.svg" },
];

export function DashboardPage() {
  const { accessToken } = useAccountAuth();
  const os = useMemo(() => detectOs(), []);
  const [sub, setSub] = useState<SubscriptionView | null>(null);
  const [apps, setApps] = useState<CatalogApp[]>([]);
  const [state, setState] = useState<LoadState>("loading");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyFile, setBusyFile] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState("loading");
    setError(null);
    try {
      const [subscription, catalog] = await Promise.all([
        getSubscription(accessToken),
        getDownloadCatalog(accessToken).catch(() => ({ apps: [] as CatalogApp[] })),
      ]);
      setSub(subscription);
      setApps(catalog.apps);
      setState("ready");
    } catch (e) {
      setSub(null);
      setApps([]);
      setError(accountErrorMessage(e));
      setState("error");
    }
  }, [accessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const download = async (asset: DownloadAsset) => {
    setBusyFile(asset.filename);
    setActionError(null);
    try {
      await saveDownload(asset.download_path, asset.filename, accessToken);
    } catch (e) {
      setActionError(accountErrorMessage(e));
    } finally {
      setBusyFile(null);
    }
  };

  const checkout = async (product: "advisor" | "ai_lotse") => {
    setBusy(true);
    setActionError(null);
    try {
      const { url } = await startCheckout(accessToken, product);
      window.location.href = url;
    } catch (e) {
      setActionError(accountErrorMessage(e));
      setBusy(false);
    }
  };

  if (state === "loading") return <Loading label="Konto wird geladen" />;

  if (state === "error" || !sub) {
    return (
      <Panel>
        <Row
          title="Konto nicht verfügbar"
          detail={error}
          action={<button type="button" className="btn-secondary" onClick={() => void load()}>Erneut versuchen</button>}
        />
      </Panel>
    );
  }

  const app = apps.find((entry) => entry.entitled && entry.assets.length > 0);
  const here = app ? bestInstaller(app.assets, os) : null;
  const hereName = os === "mac" ? "macOS" : "Windows";

  return (
    <div className="page" data-testid="account-overview">
      <MembershipDocument
        sub={sub}
        version={app?.version ?? null}
        actions={
          sub.entitled ? (
            <>
              {here ? (
                <button
                  type="button"
                  className="btn-primary"
                  disabled={busyFile === here.filename}
                  onClick={() => void download(here)}
                >
                  <IconDownload />
                  {busyFile === here.filename ? "Wird geladen …" : `Für ${hereName} laden`}
                </button>
              ) : null}
              <Link to="/downloads" className={here ? "btn-secondary" : "btn-primary"}>Alle Downloads</Link>
              <Link to="/billing" className="btn-quiet">Mitgliedschaft verwalten</Link>
            </>
          ) : (
            <>
              <Link to="/billing" className="btn-primary">Tarif wählen</Link>
              <a className="btn-quiet" href={`mailto:${SUPPORT_EMAIL}`}>Welcher Tarif passt?</a>
            </>
          )
        }
      />

      {actionError ? <Notice tone="error">{actionError}</Notice> : null}

      <Section title="Geräte">
        <Panel>
          {DESKTOPS.map((device) => {
            const asset = app ? bestInstaller(app.assets, device.os) : null;
            return (
              <Row
                key={device.os}
                mark={<img src={device.logo} alt="" />}
                title={device.title}
                detail={
                  asset && app ? (
                    <MetaLine text={installerMeta(asset, app.published_at)} />
                  ) : (
                    "Installer nach der Freischaltung"
                  )
                }
                action={
                  asset ? (
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={busyFile === asset.filename}
                      onClick={() => void download(asset)}
                    >
                      <IconDownload />
                      {busyFile === asset.filename ? "Wird geladen …" : "Laden"}
                    </button>
                  ) : null
                }
              />
            );
          })}
          <Row
            mark={<img src="/images/platforms/apple.svg" alt="" />}
            title="iPhone"
            detail="Capital Pilot im App Store"
            action={
              <a className="store-badge" href={APP_STORE_URL} target="_blank" rel="noreferrer">
                <img src="/images/platforms/app-store-de.svg" alt="Laden im App Store" />
              </a>
            }
          />
        </Panel>
      </Section>

      <Section title="Ergänzungen">
        <Panel>
          {sub.entitled && isCustomerTier(sub.product_code) && sub.product_code !== "customer_premium" ? (
            <Row
              title={sub.product_code === "customer" ? "Mehr Immobilien und Konten" : "Ohne Grenzen mit Premium"}
              detail={
                sub.product_code === "customer"
                  ? "Plus und Premium erweitern die Zahl der Immobilien und verknüpften Bankkonten."
                  : "Premium hebt die Grenzen für Immobilien und Bankkonten auf."
              }
              action={<Link to="/billing" className="btn-secondary">Stufen vergleichen</Link>}
            />
          ) : null}
          {sub.entitled && isCustomerTier(sub.product_code) ? (
            <Row
              mark={
                <span className="advisor-mark-s" aria-hidden="true">
                  <IconBriefcase />
                </span>
              }
              title="Capital Pilot Advisor"
              detail={planBlurb("advisor")}
              meta={PRICE_BEFORE_PAYMENT}
              action={
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={busy || !productReady(sub, "advisor")}
                  onClick={() => void checkout("advisor")}
                >
                  {productReady(sub, "advisor") ? (busy ? "Wird geöffnet …" : "Wechseln") : "Demnächst"}
                </button>
              }
            />
          ) : null}
          {sub.entitled && !sub.ai_lotse.entitled ? (
            <Row
              mark={<LotseMark size="s" />}
              title="KI-Lotse"
              detail={planBlurb("ai_lotse")}
              meta={<span className="data">{LOTSE_PRICE}</span>}
              action={
                <button
                  type="button"
                  className="btn-ai"
                  disabled={busy || !productReady(sub, "ai_lotse")}
                  onClick={() => void checkout("ai_lotse")}
                >
                  {productReady(sub, "ai_lotse") ? "Buchen" : "Demnächst"}
                </button>
              }
            />
          ) : null}
          <Row
            title="Weiterer YubiKey"
            detail="YubiKey 5 NFC öffnet den Tresor am Rechner und am iPhone. Verkauf bei Yubico."
            action={
              <a className="btn-secondary" href={YUBIKEY_SHOP_URL} target="_blank" rel="noreferrer">
                Bei Yubico
                <IconExternal />
              </a>
            }
          />
        </Panel>
      </Section>
    </div>
  );
}
